import { DateTime } from "luxon";
import { callApi, RequestType } from "../fetch";
import { showToast } from "../toast";
import { answerUnclear, askBookingService, followRequest, newRequestId, refusalMessage, Refusals } from "./booking_service";
import { UserModel } from "./user_repository";

// EV bays can only be booked through the booking service, and only at companies that have switched over
// and turned EV on. OnePark asks the service about each person before offering them an EV booking.

export type BookingInterval = { startTime: string; endTime: string };

// Whether the person's company takes EV bookings right now. Anything but a clear yes hides the choice.
export const takesEvBookings = async (person: Pick<UserModel, "userId" | "capacityTenantId">): Promise<boolean> =>
    (await askBookingService(person)).evEnabled;

// The service takes Sydney wall-clock times written with a trailing Z and milliseconds ("sydney-wall-v1").
const wallTime = (day: DateTime, time: string): string => {
    const clock = DateTime.fromFormat(time, "h:mm a", { locale: "en-US" });
    return day.set({ hour: clock.hour, minute: clock.minute }).toFormat("yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");
};

// One interval per booking day, skipping weekends when the company doesn't book them (as multi-day bookings do).
export const bookingDays = (startDate: Date, endDate: Date, startTime: string, endTime: string, includeWeekends: boolean): BookingInterval[] => {
    const days: BookingInterval[] = [];
    const last = DateTime.fromJSDate(endDate).startOf("day");
    for (let day = DateTime.fromJSDate(startDate).startOf("day"); day <= last; day = day.plus({ days: 1 })) {
        if (!includeWeekends && day.weekday > 5) continue;
        const start = wallTime(day, startTime);
        const end = wallTime(day, endTime);
        // An end of 12:00 AM means midnight at the end of the day, which the service takes.
        days.push({ startTime: start, endTime: end <= start && end.endsWith("T00:00:00.000Z") ? wallTime(day.plus({ days: 1 }), endTime) : end });
    }
    return days;
};

const EV_REFUSALS: Refusals = {
    ev_not_enabled: (name) => `EV bays aren't open for ${name}'s company right now, so we couldn't book one.`,
    capacity_exhausted: () => "All the EV bays are taken for that time. Try another time or day.",
    bay_configuration_review: () => "The EV bays are being set up right now. Please try again a little later.",
};

const refuse = (name: string, code?: string): false => {
    showToast(refusalMessage(code, name, `We couldn't book an EV bay for ${name} just now. Please try again.`, EV_REFUSALS), { type: "error" });
    return false;
};

type CreateResult = { data?: { status?: string } };

// Books an EV bay for the person over the given days. Resolves true once it's booked (or with the car park
// to confirm), false after telling the admin why it wasn't.
export const createEvBooking = async (
    person: Pick<UserModel, "userId" | "name" | "vehicles"> & { gallagherId?: string },
    intervals: BookingInterval[],
    { pollMs = 2000, polls = 15, timeType = "Custom" }: { pollMs?: number; polls?: number; timeType?: "FullDay" | "Custom" } = {},
): Promise<boolean> => {
    const name = person.name || "this person";
    // Someone with no registered vehicle gets in with their access card, as for a Standard booking.
    const plate = (person.vehicles ?? []).filter((value) => typeof value === "string" && value.trim().length > 0);
    if (plate.length === 0 && !(typeof person.gallagherId === "string" && person.gallagherId.trim())) {
        showToast(`${name} has no registered vehicle or access card, so they couldn't get in. Add one to their profile first.`, { type: "warning" });
        return false;
    }
    if (intervals.length === 0) {
        showToast("There are no bookable days in that range. Choose some weekdays.", { type: "warning" });
        return false;
    }
    if (intervals.some((interval) => interval.endTime <= interval.startTime)) {
        showToast("The end time needs to be after the start time.", { type: "warning" });
        return false;
    }
    const draft = { intervals, plate, parkingSpaceType: "ev", timeType, timeEncoding: "sydney-wall-v1" };
    const prepared = await callApi<{ data?: { tenantId?: string; gateEpoch?: number } }>("bookingApiV2/bookings/prepare-create",
        { ...draft, userId: person.userId }, RequestType.POST, { signedIn: true });
    const tenantId = prepared.data?.data?.tenantId;
    const gateEpoch = prepared.data?.data?.gateEpoch;
    if (prepared.error || !tenantId || typeof gateEpoch !== "number") return refuse(name, prepared.code);

    const requestId = newRequestId("ev");
    const route = intervals.length === 1 ? "bookings/create" : "series/create";
    const created = await callApi<CreateResult>(`bookingApiV2/${route}`,
        { ...draft, tenantId, gateEpoch, requestId, userId: person.userId }, RequestType.POST, { signedIn: true });
    // Without a clear refusal the booking may still have been saved, so ask about this request before saying it failed.
    const unsure = answerUnclear(created);
    if (created.error && !unsure) return refuse(name, created.code);

    // The booking is saved; the car park may still be confirming it.
    const status = await followRequest("booking-operations/status", requestId, person.userId, unsure ? "pending" : created.data?.data?.status,
        { pollMs, polls });
    if (status === "succeeded") {
        showToast(`EV bay booked for ${name}.`, { type: "success" });
        return true;
    }
    if (status === "pending") {
        showToast(`The car park is still confirming ${name}'s EV booking. It'll show in Bookings once it's confirmed.`, { type: "info" });
        return true;
    }
    return refuse(name, status === "failed" ? "provider_refused" : undefined);
};
